/**
 * SchemaAccessPanel — validates that the pipeline's saved source/target
 * credentials can actually READ table/column metadata, not just connect.
 *
 * Complements PipelineForm's Test Source/Target Connection buttons (which
 * only prove a connection succeeds) with the deeper check requested by
 * FR — "validate that the user has access to the schema defined for the
 * source and target connection" before proposing any mapping.
 *
 * Mirrors PipelineForm's Test Connection UX exactly: single loading state,
 * a result panel that never blocks on the other side's outcome, and a
 * warning (not error) when connected but zero tables are reachable.
 */

import { useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import type { SchemaAccessValidationResult } from "../../features/pipeline-engineer/types";
import { validateSchemaAccess } from "../../features/pipeline-engineer/api/pe-client";

interface SchemaAccessPanelProps {
  pipelineId: string;
}

export function SchemaAccessPanel({ pipelineId }: SchemaAccessPanelProps) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SchemaAccessValidationResult | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  const handleValidate = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await validateSchemaAccess(pipelineId);
      setResult(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Schema access validation failed.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => void handleValidate()}
        disabled={loading}
        aria-label="Validate schema access"
        className="flex items-center gap-2 px-3 py-1.5 bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? (
          <Loader2 size={12} className="animate-spin" aria-hidden="true" />
        ) : (
          <ShieldCheck size={12} aria-hidden="true" />
        )}
        Validate Schema Access
      </button>

      {error !== null && (
        <p className="text-red-400 text-sm" role="alert">
          {error}
        </p>
      )}

      {result !== null && (
        <div role="status" aria-live="polite" className="space-y-1.5">
          <SchemaAccessSideRow label="Source" result={result.source} />
          <SchemaAccessSideRow label="Target" result={result.target} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component
// ---------------------------------------------------------------------------

function SchemaAccessSideRow({
  label,
  result,
}: {
  label: string;
  result: SchemaAccessValidationResult["source"];
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-2 text-sm">
        <span aria-hidden="true">{result.connected ? "✅" : "❌"}</span>
        <span className={result.connected ? "text-green-400" : "text-red-400"}>
          {label}:{" "}
          {result.connected
            ? `${result.tableCount} table${result.tableCount !== 1 ? "s" : ""} visible`
            : "access failed"}
        </span>
      </div>
      {result.error !== null && (
        <p className="text-xs text-white/40 ml-6">{result.error}</p>
      )}
      {result.warning !== null && (
        <p className="text-xs text-amber-400/80 ml-6">{result.warning}</p>
      )}
    </div>
  );
}
